import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, withRouter, RouteComponentProps } from 'react-router-dom';
import { Collapse } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import {
  Bell as IconBell,
  Users as IconUsers,
  BarChart as IconBarChart,
  Award as IconAward,
  List as IconList,
  Settings as IconSettings,
  Home as IconHome,
  Package as IconPackage,
  Truck as IconTruck,
  Archive as IconArchive,
  Clipboard as IconClipboard,
  BarChart2 as IconBarChart2
} from 'react-feather';

import RoutesMiddleware from '~/routes/middleware';
import { ROUTES_VALIDATIONS, USER_ROLE } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import { AppStateType } from '~/store/modules/root-reducer';
import { NavigationType, NavigationChildType } from '~/types/navigation-types';
import {
  Box,
  Brand,
  BrandIcon,
  Category,
  CategoryBadge,
  CategoryIconLess,
  CategoryIconMore,
  CategoryText,
  Drawer,
  Items,
  Link,
  LinkBadge,
  LinkText,
  List,
  Scrollbar,
  SidebarFooter,
  SidebarFooterIcon,
  SidebarFooterSubText,
  SidebarFooterText,
  // SidebarFooter,
  // SidebarFooterBadge,
  // SidebarFooterSubText,
  // SidebarFooterText,
  SidebarSection
} from './styles';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';
import { Assignment } from '@material-ui/icons';

type SidebarCategoryPropsType = {
  name: string;
  icon?: JSX.Element;
  classes?: string;
  isOpen?: boolean;
  isCollapsable: boolean;
  badge?: string | number;
  activeClassName?: string;
  button: true;
  onClick?: () => void;
  to?: string;
  component?: typeof NavLink;
  exact?: boolean;
};

const SidebarCategory: React.FunctionComponent<SidebarCategoryPropsType> = ({
  name,
  icon,
  classes,
  isOpen,
  isCollapsable,
  badge,
  ...rest
}: any) => {
  return (
    <Category {...rest}>
      {icon}
      <CategoryText>{name}</CategoryText>
      {isCollapsable ? isOpen ? <CategoryIconMore /> : <CategoryIconLess /> : null}
      {badge ? <CategoryBadge label={badge} /> : ''}
    </Category>
  );
};

type SidebarLinkPropsType = {
  name: string;
  to: string;
  badge?: string | number;
  icon?: JSX.Element;
  handleClick?: () => void;
};

const SidebarLink: React.FunctionComponent<SidebarLinkPropsType> = ({ name, to, badge, handleClick }) => {
  return (
    <Link button dense component={NavLink} exact to={to} activeClassName="active" onClick={handleClick}>
      <LinkText>{name}</LinkText>
      {badge ? <LinkBadge label={badge} /> : ''}
    </Link>
  );
};

type SidebarPropsType = {
  classes?: string;
  staticContext: string;
  location: {
    pathname: string;
  };
  PaperProps: {
    style: {
      width: number;
    };
  };
  variant?: 'permanent' | 'persistent' | 'temporary';
  open?: boolean;
  onClose?: () => void;
};

const Sidebar: React.FunctionComponent<RouteComponentProps & SidebarPropsType> = ({
  classes,
  staticContext,
  location,
  ...rest
}) => {
  type tplotOptions = {
    [key: number]: boolean;
  };

  const [routes, setRoutes] = useState<Array<NavigationType>>([]);
  const configuration: ConfigurationInterface = useSelector((state: AppStateType) => state.configuration);
  const tenant = useSelector((state: AppStateType) => state.tenant);
  const { user } = useSelector((state: AppStateType) => state?.auth);

  const routesValidations = new RoutesMiddleware();

  const initOpenRoutes = (): tplotOptions => {
    /* Open collapse element that matches current url */
    const pathName = location.pathname;

    let _routes = {};

    if (routes?.length > 0) {
      routes.forEach((route: NavigationType, index) => {
        const isActive = pathName.indexOf(route.path) === 0;
        const isOpen = route.open;
        const isHome = route.containsHome && pathName === '/';

        _routes = { ..._routes, [index]: isActive || isOpen || isHome };
      });
    }

    return _routes;
  };

  const [openRoutes, setOpenRoutes] = useState(() => initOpenRoutes());

  const toggle = (index: number) => {
    // Collapse all elements
    Object.keys(openRoutes).forEach(item => openRoutes[index] || setOpenRoutes(state => ({ ...state, [item]: false })));

    // Toggle selected element
    setOpenRoutes(state => ({ ...state, [index]: !state[index] }));
  };

  useEffect(() => {
    setRoutes([
      {
        id: formatMessage('sidebarMenuItemDashboard'),
        path: '/dashboard',
        icon: <IconBarChart />,
        containsHome: true,
        show: true,
        children: [
          {
            path: '/dashboard/analytics',
            name: formatMessage('sidebarMenuItemDashboardAnalytics'),
            component: null,
            show: true
          },
          {
            path: '/dashboard/client-visit',
            name: formatMessage('sidebarMenuItemDashboardClientVisit'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole,
              extraValidation: (configuration?.allowVendorTracking || configuration?.allowManualVendorTracking) ?? false
            })
          },
          {
            path: '/dashboard/indicators',
            name: formatMessage('sidebarMenuItemDashboardIndicators'),
            component: null,
            show: true
          },
          ...(configuration?.personalDashboards ?? []).map(({ personalDashboardId, personalDashboardName }) => ({
            path: `/dashboard/custom/${personalDashboardId}?name=${encodeURIComponent(personalDashboardName)}`,
            name: personalDashboardName,
            component: null,
            show: true
          }))
          // {
          //   path: '/dashboard/telemetria',
          //   name: formatMessage('sidebarMenuItemDashboardTelemetria'),
          //   component: null,
          //   show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          //     requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          //     currentRole: user?.userRole
          //   })
          // },
          // {
          //   path: '/dashboard/recommendations',
          //   name: formatMessage('sidebarMenuItemDashboardRecommendations'),
          //   component: null,
          //   show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          //     requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          //     currentRole: user?.userRole
          //   })
          // },
          // {
          //   path: '/dashboard/positivation-sellers',
          //   name: formatMessage('sidebarMenuItemDashboardPositivationSellers'),
          //   component: null,
          //   show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          //     requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          //     currentRole: user?.userRole
          //   })
          // },
          // {
          //   path: '/dashboard/efficiency-sales-potential',
          //   name: formatMessage('sidebarMenuItemDashboardEfficiencySalesPotential'),
          //   component: null,
          //   show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          //     requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          //     currentRole: user?.userRole
          //   })
          // }
        ],
        component: null
      },
      {
        id: formatMessage('productsTitle'),
        path: '/products',
        icon: <IconArchive />,
        containsHome: true,
        children: null,
        component: null,
        show: true
      },
      {
        id: formatMessage('sidebarMenuItemOrder'),
        path: '/order',
        icon: <IconList />,
        containsHome: true,
        children: null,
        component: null,
        show: true
      },
      {
        id: formatMessage('sidebarMenuItemSalesTracking'),
        path: '/sales-tracking',
        icon: <IconBarChart2 />,
        containsHome: true,
        children: null,
        component: null,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.VENDOR, USER_ROLE.TELEMARKETING],
          currentRole: user?.userRole,
          extraValidation: configuration?.allowSalesTracking
        })
      },
      {
        id: formatMessage('sidebarMenuItemLoadControl'),
        path: '/load-control',
        icon: <IconTruck />,
        containsHome: true,
        children: null,
        component: null,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          currentRole: user?.userRole,
          extraValidation: configuration?.useLoadControl
        })
      },
      {
        id: formatMessage('sidebarMenuItemEngine'),
        path: '/engine',
        icon: <IconAward />,
        containsHome: true,
        open: true,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
          currentRole: user?.userRole
        }),
        children: [
          //! Caio pediu para comentar, irá criar uma task sobre o mesmo
          // {
          //   path: '/engine/rewards',
          //   name: formatMessage('sidebarMenuItemRewards'),
          //   component: null,
          //   show: true
          // },
          {
            path: '/engine/trading',
            name: formatMessage('sidebarMenuItemTrading'),
            component: null,
            show: configuration?.useNegotiationEngine
          },
          {
            path: '/engine/sponsored',
            name: formatMessage('sidebarMenuItemSponsored'),
            component: null,
            show: true
          },
          {
            path: '/engine/promotion',
            name: formatMessage('sidebarMenuItemPromotion'),
            component: null,
            show: true
          },

          {
            path: '/engine/mandatory-mix',
            name: formatMessage('sidebarMenuItemMandatoryMix'),
            component: null,
            show: configuration?.useMandatoryMix
          }
        ],
        component: null
      },
      {
        id: formatMessage('sidebarMenuItemAccess'),
        path: '/access',
        icon: <IconUsers />,
        containsHome: true,
        open: true,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          currentRole: user?.userRole
        }),
        children: [
          // {
          //   path: '/access/profile',
          //   name: formatMessage('sidebarMenuItemAccessProfile'),
          //   component: null
          // },
          {
            path: '/access/team',
            name: formatMessage('sidebarMenuItemUserGroup'),
            component: null,
            show: true
          },
          {
            path: '/access/user',
            name: formatMessage('sidebarMenuItemUser'),
            component: null,
            show: true
          }
        ],
        component: null
      },
      {
        id: formatMessage('sidebarMenuItemRegister'),
        path: '/register',
        icon: <IconPackage />,
        containsHome: true,
        open: true,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [
            USER_ROLE.DIRECTOR,
            USER_ROLE.TECH,
            USER_ROLE.VENDOR,
            USER_ROLE.TELEMARKETING,
            USER_ROLE.MANAGER,
            USER_ROLE.SUPERVISOR,
            USER_ROLE.MAINTENANCE
          ],
          currentRole: user?.userRole
        }),
        children: [
          {
            path: '/register/reason',
            name: formatMessage('sidebarMenuItemReason'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/order-type',
            name: formatMessage('sidebarMenuItemOrderType'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/load-type',
            name: formatMessage('sidebarMenuItemLoadType'),
            component: null,
            show: routesValidations.routeToDisplay(
              [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.LOAD_CONTROL],
              {
                requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
                currentRole: user?.userRole,
                extraValidation: configuration?.useLoadControl
              }
            )
          },
          {
            path: '/register/order-split',
            name: formatMessage('sidebarMenuItemOrderSplit'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/product-photos',
            name: formatMessage('sidebarMenuItemProductPhotos'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/client',
            name: formatMessage('sidebarMenuItemClient'),
            component: null,
            show: routesValidations.routeToDisplay(
              [ROUTES_VALIDATIONS.REQUESTED_ROLES, ROUTES_VALIDATIONS.ALLOW_COSTUMER_REGISTRATION],
              {
                requiredRoles: [
                  USER_ROLE.VENDOR,
                  USER_ROLE.TELEMARKETING,
                  USER_ROLE.SUPERVISOR,
                  USER_ROLE.MANAGER,
                  USER_ROLE.DIRECTOR
                ],
                currentRole: user?.userRole,
                extraValidation: configuration?.allowCustomerRegistration
              }
            )
          },
          {
            path: '/register/client-checklist',
            name: formatMessage('sidebarMenuItemClientChecklist'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation: configuration?.useClientChecklist ?? false
            })
          },
          {
            path: '/register/justifications',
            name: formatMessage('sidebarMenuItemJustifications'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole,
              extraValidation: configuration?.allowListJustification ?? false
            })
          },
          {
            path: '/register/product-bonification',
            name: formatMessage('sidebarMenuItemProductBonification'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/focus-supplier',
            name: formatMessage('sidebarMenuItemFocusSupplier'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole,
              extraValidation: configuration?.useTagFocus ?? false
            })
          },
          {
            path: '/register/daily-route',
            name: formatMessage('sidebarMenuItemDailyRoute'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH, USER_ROLE.MANAGER, USER_ROLE.SUPERVISOR],
              currentRole: user?.userRole
            })
          },
          {
            path: '/register/routing/list',
            name: formatMessage('sidebarMenuItemRoutingPlanning'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.VENDOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation:
                user?.userRole === USER_ROLE.VENDOR && configuration?.useCommercialRoute
                  ? configuration?.allowSalespersonCreateRouting ?? false
                  : configuration?.useCommercialRoute ?? false
            })
          },
          {
            path: '/register/product-technical-data',
            name: formatMessage('sidebarMenuItemProductTechnicalData'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          }
        ],
        component: null
      },
      {
        id: 'Relatórios',
        path: '/reports',
        icon: <IconClipboard />,
        containsHome: true,
        open: true,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [
            USER_ROLE.DIRECTOR,
            USER_ROLE.TECH,
            USER_ROLE.MANAGER,
            USER_ROLE.SUPERVISOR,
            USER_ROLE.VENDOR,
            USER_ROLE.MAINTENANCE
          ],
          currentRole: user?.userRole
        }),
        children: [
          {
            path: '/reports/rating',
            name: formatMessage('sidebarMenuItemClientRatings'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation: configuration?.useClientRating ?? false
            })
          },
          {
            path: '/reports/client-visit',
            name: formatMessage('sidebarMenuItemClientVisit'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation: (configuration?.allowVendorTracking || configuration?.allowManualVendorTracking) ?? false
            })
          },
          {
            path: '/reports/control-notifications',
            name: 'Controle de Notificações',
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation: configuration?.usePersistentNotification ?? false
            })
          },
          {
            path: '/reports/client-checklist-report',
            name: formatMessage('sidebarMenuItemClientChecklistReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole,
              extraValidation: configuration?.useClientChecklist ?? false
            })
          },
          {
            path: '/reports/promotions-kit',
            name: formatMessage('sidebarMenuItemPromotionsKits'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          },
          {
            path: '/reports/daily-route',
            name: formatMessage('sidebarMenuItemDailyRouteReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          },
          {
            path: '/reports/vendor-client-visit',
            name: formatMessage('sidebarMenuItemClientVisityAndSalesReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          },
          {
            path: '/reports/commission',
            name: formatMessage('sidebarMenuItemComissionReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.VENDOR],
              currentRole: user?.userRole,
              extraValidation: configuration?.allowUserCommissionReport
            })
          },
          {
            path: '/reports/client-position',
            name: formatMessage('sidebarMenuItemClientPositionReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.VENDOR],
              currentRole: user?.userRole
            })
          },
          {
            path: '/reports/routed-clients',
            name: formatMessage('sidebarMenuItemRoutedClientsReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          },
          {
            path: '/reports/product-without-picture',
            name: formatMessage('sidebarMenuItemProductWithoutPictureReport'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [
                USER_ROLE.DIRECTOR,
                USER_ROLE.TECH,
                USER_ROLE.MANAGER,
                USER_ROLE.SUPERVISOR,
                USER_ROLE.MAINTENANCE
              ],
              currentRole: user?.userRole
            })
          }
        ],
        component: null
      },
      {
        id: formatMessage('sidebarMenuItemNotification'),
        path: '/notifications',
        icon: <IconBell />,
        containsHome: true,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.MANAGER, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
          currentRole: user?.userRole
        }),
        children: [
          {
            path: '/notifications/send',
            name: formatMessage('notificationLabelSend'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.MANAGER, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
              currentRole: user?.userRole
            })
          },
          {
            path: '/notifications',
            name: formatMessage('notificationLabelList'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.MANAGER, USER_ROLE.TECH, USER_ROLE.MAINTENANCE],
              currentRole: user?.userRole
            })
          }
        ],
        component: null
      },
      {
        id: formatMessage('sidebarMenuItemAudits'),
        path: '/audits',
        icon: <Assignment />,
        containsHome: true,
        component: null,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          currentRole: user?.userRole
        }),
        children: [
          {
            path: '/audits/user',
            name: formatMessage('sidebarMenuItemAuditsUser'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
              currentRole: user?.userRole
            })
          }
        ]
      },
      {
        id: formatMessage('sidebarMenuItemConfiguration'),
        path: '/configuration',
        icon: <IconSettings />,
        containsHome: true,
        component: null,
        show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
          requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
          currentRole: user?.userRole
        }),
        children: [
          {
            path: '/configuration/app',
            name: formatMessage('sidebarMenuItemConfigurationApp'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
              currentRole: user?.userRole
            })
          },
          {
            path: '/configuration/infrastructure',
            name: formatMessage('sidebarMenuItemConfigurationInfrastructure'),
            component: null,
            show: routesValidations.routeToDisplay([ROUTES_VALIDATIONS.REQUESTED_ROLES], {
              requiredRoles: [USER_ROLE.DIRECTOR, USER_ROLE.TECH],
              currentRole: user?.userRole
            })
          }
        ]
      }
    ]);
  }, [configuration, user?.userRole]);

  return (
    <Drawer variant="permanent" {...rest}>
      <Brand component={true ? null : NavLink} to="/" button>
        <BrandIcon />
        <Box ml={1}>iSA Backoffice</Box>
      </Brand>
      <Scrollbar>
        <List disablePadding>
          <Items>
            {routes.map((category: NavigationType, index) => (
              <React.Fragment key={index}>
                {category.show && (
                  <>
                    {category.header ? <SidebarSection>{category.header}</SidebarSection> : null}

                    {category.children && category.children.some(child => child.show) ? (
                      <React.Fragment key={index}>
                        <SidebarCategory
                          isOpen={!openRoutes[index]}
                          isCollapsable
                          name={category.id}
                          icon={category.icon}
                          button
                          onClick={() => toggle(index)}
                        />

                        <Collapse in={openRoutes[index]} timeout="auto" unmountOnExit>
                          {category.children.map((route: NavigationChildType, index: number) => (
                            <>
                              {route.show && (
                                <SidebarLink
                                  key={index}
                                  name={route.name}
                                  to={route.path}
                                  icon={route.icon}
                                  badge={route.badge}
                                  handleClick={rest.onClose}
                                />
                              )}
                            </>
                          ))}
                        </Collapse>
                      </React.Fragment>
                    ) : !category.children ? (
                      <React.Fragment>
                        <SidebarCategory
                          isCollapsable={false}
                          name={category.id}
                          to={category.path}
                          activeClassName="active"
                          component={NavLink}
                          icon={category.icon}
                          exact
                          button
                          badge={category.badge}
                          onClick={rest.onClose}
                        />
                      </React.Fragment>
                    ) : null}
                  </>
                )}
              </React.Fragment>
            ))}
          </Items>
        </List>
      </Scrollbar>
      <SidebarFooter
        component={[USER_ROLE.DIRECTOR, USER_ROLE.TECH].includes(user?.userRole) ? NavLink : null}
        to="/tenant"
        button
      >
        <Grid container spacing={2}>
          <Grid item>
            <SidebarFooterIcon>
              <IconHome />
            </SidebarFooterIcon>
          </Grid>
          <Grid item>
            <SidebarFooterText variant="body2">{tenant?.tenantInfo?.companyName}</SidebarFooterText>
            <SidebarFooterSubText variant="caption">
              {tenant?.addressInfo?.city}/{tenant?.addressInfo?.state}
            </SidebarFooterSubText>
          </Grid>
        </Grid>
      </SidebarFooter>
    </Drawer>
  );
};

export default withRouter(Sidebar);
